import { Form, Field, StringField, Int64Field, StringSliceField, FileField } from 'pbts/c1/api/form/v1/form'
import { IntlShape } from 'react-intl'
import * as yup from 'yup'
import { isValueFilled, FieldConstraints, parseConstraints, buildRevalidationMap, RecordStringAny } from './formStructure'
import { isFieldValueProvidedByAdmin, isFieldVisibleToUser } from './providerConfigUtils'

export type { RecordStringAny }

export enum FORM_FIELD_KINDS {
    STRING = 'stringField',
    BOOL = 'boolField',
    STRING_SLICE = 'stringSliceField',
    INT64 = 'int64Field',
    FILE = 'fileField',
}

export function c1FormToYupSchema(form: Form, intl: IntlShape): yup.ObjectSchema<RecordStringAny> {
    return buildFormSchema(form, intl).schema
}

function createTypeSchema(field: Field, intl: IntlShape): yup.Schema {
    switch (field.type.oneofKind) {
        case FORM_FIELD_KINDS.STRING: return stringSchema(field.type.stringField, intl)
        case FORM_FIELD_KINDS.INT64: return int64Schema(field.type.int64Field, field.displayName, intl)
        case FORM_FIELD_KINDS.BOOL: return yup.boolean()
        case FORM_FIELD_KINDS.STRING_SLICE: return stringSliceSchema(field.type.stringSliceField, intl)
        case FORM_FIELD_KINDS.FILE: return fileSchema(field.type.fileField, intl)
        default: return yup.mixed()
    }
}

export interface FormSchemaResult {
    schema: yup.ObjectSchema<RecordStringAny>
    revalidationMap: Map<string, string[]>
}

export function buildFormSchema(form: Form, intl: IntlShape): FormSchemaResult {
    const constraints = parseConstraints(form.fieldRelationships)
    const revalidationMap = buildRevalidationMap(constraints)

    // Build display name lookup
    const displayNames = new Map<string, string>()
    for (const field of form.fields) {
        displayNames.set(field.name, field.displayName || field.name)
    }

    const fields: Record<string, yup.Schema> = {}
    for (const field of form.fields) {
        let schema = createTypeSchema(field, intl)
        // Only apply required validation if field is visible to user and not admin-provided
        // Admin fields have their values computed on the backend (CEL or static default)
        if (field.required && isFieldVisibleToUser(field) && !isFieldValueProvidedByAdmin(field)) {
            const deps = constraints.dependsOn.get(field.name)
            // If field has dependencies, only require it when dependencies are met (field is visible)
            schema = schema.test('required', intl.formatMessage({ id: 'dynamic_form_required', defaultMessage: '{field} is required' }, { field: field.displayName }), function testRequired(v): boolean {
                if (deps?.length && !deps.every((d) => isValueFilled(this.parent[d]))) return true // Hidden, skip validation
                return isValueFilled(v)
            })
        }
        fields[field.name] = applyRelationships(schema, field.name, constraints, displayNames, intl)
    }

    return { schema: yup.object(fields) as yup.ObjectSchema<RecordStringAny>, revalidationMap }
}

function stringSchema(field: StringField, intl: IntlShape): yup.StringSchema {
    const r = field.rules
    let s = yup.string().trim().transform((v) => (v === '' ? undefined : v))

    if (field.view.oneofKind === 'selectField') {
        const opts = field.view.selectField.options.map((o) => o.value)
        return s.test('validOption', intl.formatMessage({ id: 'dynamic_form_invalidOption', defaultMessage: 'Invalid option' }), (v) => !v || opts.includes(v))
    }

    if (!r) return s

    if (r.const !== undefined) {
        s = s.oneOf([r.const], intl.formatMessage({ id: 'dynamic_form_const', defaultMessage: 'Must be {value}' }, { value: r.const }))
    }
    if (r.len !== undefined) {
        s = s.length(Number(r.len), intl.formatMessage({ id: 'dynamic_form_len', defaultMessage: 'Must be {n} characters' }, { n: r.len }))
    }
    if (r.minLen !== undefined) {
        s = s.min(Number(r.minLen), intl.formatMessage({ id: 'dynamic_form_minLen', defaultMessage: 'Must be at least {n} characters' }, { n: r.minLen }))
    }
    if (r.maxLen !== undefined) {
        s = s.max(Number(r.maxLen), intl.formatMessage({ id: 'dynamic_form_maxLen', defaultMessage: 'Must be at most {n} characters' }, { n: r.maxLen }))
    }
    if (r.pattern) {
        s = s.matches(new RegExp(r.pattern), intl.formatMessage({ id: 'dynamic_form_pattern', defaultMessage: 'Invalid format' }))
    }
    if (r.prefix) {
        const prefix = r.prefix
        s = s.test('prefix', intl.formatMessage({ id: 'dynamic_form_prefix', defaultMessage: 'Must start with {prefix}' }, { prefix }), (v) => !v || v.startsWith(prefix))
    }
    if (r.suffix) {
        const suffix = r.suffix
        s = s.test('suffix', intl.formatMessage({ id: 'dynamic_form_suffix', defaultMessage: 'Must end with {suffix}' }, { suffix }), (v) => !v || v.endsWith(suffix))
    }
    if (r.contains) {
        const contains = r.contains
        s = s.test('contains', intl.formatMessage({ id: 'dynamic_form_contains', defaultMessage: 'Must contain {value}' }, { value: contains }), (v) => !v || v.includes(contains))
    }
    if (r.notContains) {
        const notContains = r.notContains
        s = s.test('notContains', intl.formatMessage({ id: 'dynamic_form_notContains', defaultMessage: 'Must not contain {value}' }, { value: notContains }), (v) => !v || !v.includes(notContains))
    }
    if (r.in?.length) {
        s = s.oneOf(r.in, r.in.length <= 5
            ? intl.formatMessage({ id: 'dynamic_form_in_values', defaultMessage: 'Must be one of: {values}' }, { values: r.in.join(', ') })
            : intl.formatMessage({ id: 'dynamic_form_in', defaultMessage: 'Must be one of the allowed values' }))
    }
    if (r.notIn?.length) {
        s = s.notOneOf(r.notIn, intl.formatMessage({ id: 'dynamic_form_notIn', defaultMessage: 'Value is not allowed' }))
    }

    return s
}

function int64Schema(field: Int64Field, name: string, intl: IntlShape): yup.Schema {
    const r = field.rules
    let s: yup.NumberSchema = yup.number()
        .transform((v, orig): number | undefined => (orig === '' || Number.isNaN(v) ? undefined : v))
        .integer(intl.formatMessage({ id: 'dynamic_form_integer', defaultMessage: '{field} must be a whole number' }, { field: name })) as yup.NumberSchema

    if (!r) return s

    if (r.const !== undefined) {
        s = s.oneOf([r.const], intl.formatMessage({ id: 'dynamic_form_const', defaultMessage: 'Must be {value}' }, { value: r.const }))
    }
    if (r.lt !== undefined) {
        s = s.lessThan(r.lt, intl.formatMessage({ id: 'dynamic_form_lt', defaultMessage: 'Must be less than {n}' }, { n: r.lt }))
    }
    if (r.lte !== undefined) {
        s = s.max(r.lte, intl.formatMessage({ id: 'dynamic_form_lte', defaultMessage: 'Must be at most {n}' }, { n: r.lte }))
    }
    if (r.gt !== undefined) {
        s = s.moreThan(r.gt, intl.formatMessage({ id: 'dynamic_form_gt', defaultMessage: 'Must be greater than {n}' }, { n: r.gt }))
    }
    if (r.gte !== undefined) {
        s = s.min(r.gte, intl.formatMessage({ id: 'dynamic_form_gte', defaultMessage: 'Must be at least {n}' }, { n: r.gte }))
    }
    if (r.in?.length) {
        s = s.oneOf(r.in, r.in.length <= 5
            ? intl.formatMessage({ id: 'dynamic_form_in_values', defaultMessage: 'Must be one of: {values}' }, { values: r.in.join(', ') })
            : intl.formatMessage({ id: 'dynamic_form_in', defaultMessage: 'Must be one of the allowed values' }))
    }
    if (r.notIn?.length) {
        s = s.notOneOf(r.notIn, intl.formatMessage({ id: 'dynamic_form_notIn', defaultMessage: 'Value is not allowed' }))
    }

    return s
}

function stringSliceSchema(field: StringSliceField, intl: IntlShape): yup.Schema {
    const r = field.rules
    let itemSchema = yup.string().defined()

    if (r?.items?.type.oneofKind === 'string') {
        const ir = r.items.type.string
        if (ir.minLen !== undefined) {
            itemSchema = itemSchema.min(Number(ir.minLen), intl.formatMessage({ id: 'dynamic_form_itemMinLen', defaultMessage: 'Item must be at least {n} characters' }, { n: ir.minLen }))
        }
        if (ir.maxLen !== undefined) {
            itemSchema = itemSchema.max(Number(ir.maxLen), intl.formatMessage({ id: 'dynamic_form_itemMaxLen', defaultMessage: 'Item must be at most {n} characters' }, { n: ir.maxLen }))
        }
        if (ir.pattern) {
            itemSchema = itemSchema.matches(new RegExp(ir.pattern), intl.formatMessage({ id: 'dynamic_form_itemPattern', defaultMessage: 'Item has invalid format' }))
        }
    }

    let s = yup.array().of(itemSchema)
    if (r?.minItems !== undefined) {
        s = s.min(r.minItems, intl.formatMessage({ id: 'dynamic_form_minItems', defaultMessage: 'Must have at least {n} items' }, { n: r.minItems }))
    }
    if (r?.maxItems !== undefined) {
        s = s.max(r.maxItems, intl.formatMessage({ id: 'dynamic_form_maxItems', defaultMessage: 'Must have at most {n} items' }, { n: r.maxItems }))
    }
    if (r?.unique) {
        s = s.test('unique', intl.formatMessage({ id: 'dynamic_form_unique', defaultMessage: 'Items must be unique' }), (v) => !v || new Set(v).size === v.length)
    }

    return s
}

function fileSchema(field: FileField, intl: IntlShape): yup.MixedSchema {
    let s = yup.mixed().test('is-file', intl.formatMessage({ id: 'dynamic_form_invalidFile', defaultMessage: 'Must be a valid file' }), (v) => !v || v instanceof File)

    if (field.maxFileSize !== undefined) {
        const max = field.maxFileSize
        s = s.test('max-size', intl.formatMessage({ id: 'dynamic_form_maxFileSize', defaultMessage: 'File must be smaller than {size} bytes' }, { size: max }), (v) =>
            !v || !(v instanceof File) || v.size <= max)
    }

    if (field.acceptedFileTypes?.length) {
        const types = field.acceptedFileTypes
        s = s.test('file-type', intl.formatMessage({ id: 'dynamic_form_fileType', defaultMessage: 'File must be one of: {types}' }, { types: types.join(', ') }), (v) => {
            if (!v || !(v instanceof File)) return true
            return types.some((t) => {
                if (t.startsWith('.')) return v.name.toLowerCase().endsWith(t.toLowerCase())
                if (t.includes('/*')) return v.type.startsWith(`${t.split('/')[0]}/`)
                return v.type === t
            })
        })
    }

    return s
}

function applyRelationships(
    schema: yup.Schema,
    fieldName: string,
    c: FieldConstraints,
    displayNames: Map<string, string>,
    intl: IntlShape,
): yup.Schema {
    let s = schema
    const toDisplayNames = (names: Iterable<string>): string => [...names].map((n) => displayNames.get(n) ?? n).join(', ')

    const rt = c.requiredTogether.get(fieldName)
    if (rt?.size) {
        const relatedFields = toDisplayNames(rt)
        s = s.test('requiredTogether', intl.formatMessage({ id: 'dynamic_form_requiredTogether', defaultMessage: 'Required when {fields} is set' }, { fields: relatedFields }), function testRT(v): boolean {
            return ![...rt].some((p) => isValueFilled(this.parent[p])) || isValueFilled(v)
        })
    }

    const me = c.mutuallyExclusive.get(fieldName)
    if (me?.size) {
        const exclusiveFields = toDisplayNames(me)
        s = s.test('mutuallyExclusive', intl.formatMessage({ id: 'dynamic_form_mutuallyExclusive', defaultMessage: 'Cannot be set with {fields}' }, { fields: exclusiveFields }), function testME(v): boolean {
            return !isValueFilled(v) || ![...me].some((p) => isValueFilled(this.parent[p]))
        })
    }

    const deps = c.dependsOn.get(fieldName)
    if (deps?.length) {
        const depFields = toDisplayNames(deps)
        s = s.test('dependentOn', intl.formatMessage({ id: 'dynamic_form_dependentOn', defaultMessage: 'Requires {fields} to be set first' }, { fields: depFields }), function testDep(v): boolean {
            return !isValueFilled(v) || deps.every((d) => isValueFilled(this.parent[d]))
        })
    }

    const groups = c.atLeastOne.filter((g) => g.includes(fieldName))
    if (groups.length) {
        const groupFields = toDisplayNames(groups[0])
        s = s.test('atLeastOne', intl.formatMessage({ id: 'dynamic_form_atLeastOne', defaultMessage: 'At least one of {fields} is required' }, { fields: groupFields }), function testALO(): boolean {
            return groups.every((g) => g.some((f) => isValueFilled(this.parent[f])))
        })
    }

    return s
}
